import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { MerchantProgramParticipation, ProgramV2Response } from "@getbread/api-client-program-v2";

import { useGetMerchantProgramParticipation } from "merchant-portal-data/program/useGetMerchantProgramParticipation";
import { useGetProgramsByIDs } from "merchant-portal-data/program/useGetProgramsByIDs";
import {
	hasCheckoutSDKAccess,
	isEcommerceProgram,
	isInstoreProgram,
	isVirtualCardProgram,
} from "merchant-portal-data/program/utils";
import { userProgramsSelectionKey } from "merchant-portal-frontend/src/global.constants";
import { useLocalStorage } from "merchant-portal-frontend/src/utils/localStorage";

import { useMerchant } from "./MerchantProvider";

export interface MerchantProgramsContextValue {
	activePrograms: ProgramV2Response[];
	allPrograms: ProgramV2Response[];
	merchantProgramParticipations: MerchantProgramParticipation[];
	userSelection?: UserSelection;
	setUserSelection?: (selection: UserSelection) => void;
	isFetchingMerchantPrograms: boolean;
	hasProgramEnrollment: (enrollment: MerchantProgramEnrollment) => boolean;
}

export interface UserSelection {
	programs: ProgramV2Response[];
	areAllProgramsSelected: boolean;
}

export const MerchantProgramsContext = React.createContext<MerchantProgramsContextValue>({
	activePrograms: [],
	allPrograms: [],
	merchantProgramParticipations: [],
	isFetchingMerchantPrograms: false,
	hasProgramEnrollment: () => false,
});

export type MerchantProgramEnrollment =
	| "ecommerce"
	| "ecommerce-checkout-sdk-access"
	| "instore"
	| "instore-virtual-card";

export const MerchantProgramsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const { selectedMerchantID } = useMerchant();

	const [userSelection, setUserSelection, updateUserProgramsSelectionKey] =
		useLocalStorage<UserSelection>(`${userProgramsSelectionKey}.${selectedMerchantID}`, {
			programs: [],
			areAllProgramsSelected: true,
		});

	useEffect(() => {
		updateUserProgramsSelectionKey(`${userProgramsSelectionKey}.${selectedMerchantID}`);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedMerchantID]);

	const {
		data: merchantProgramParticipationQueryResults,
		isFetchingNextPage: isFetchingProgramParticipations,
		fetchNextPage: fetchNextProgramParticipationsPage,
		hasNextPage: hasMoreProgramParticipations,
		isLoading: isLoadingProgramParticicpations,
	} = useGetMerchantProgramParticipation({ merchantID: selectedMerchantID });

	useEffect(() => {
		if (!isFetchingProgramParticipations && hasMoreProgramParticipations) {
			fetchNextProgramParticipationsPage();
		}
	}, [
		merchantProgramParticipationQueryResults,
		isFetchingProgramParticipations,
		fetchNextProgramParticipationsPage,
		hasMoreProgramParticipations,
	]);

	const merchantProgramParticipations = useMemo(() => {
		return (
			merchantProgramParticipationQueryResults?.pages?.flatMap(({ data }) => data.items) ||
			[]
		);
	}, [merchantProgramParticipationQueryResults]);

	const uniqueProgramIDs = useMemo(() => {
		return Array.from(
			new Set(merchantProgramParticipations.map(({ programID }) => programID)),
		);
	}, [merchantProgramParticipations]);

	const programQueryResults = useGetProgramsByIDs({
		programIDs: uniqueProgramIDs,
	});

	const isFetchingMerchantPrograms = useMemo(
		() =>
			!!selectedMerchantID &&
			(isLoadingProgramParticicpations ||
				hasMoreProgramParticipations ||
				programQueryResults.isLoading),
		[
			hasMoreProgramParticipations,
			isLoadingProgramParticicpations,
			programQueryResults,
			selectedMerchantID,
		],
	);

	const allPrograms = useMemo(
		() =>
			programQueryResults.isLoading ? [] : programQueryResults.data.map(({ data }) => data),
		[programQueryResults],
	);

	const activePrograms = useMemo(
		() =>
			allPrograms.filter(
				({ id }) =>
					!merchantProgramParticipations.find(({ programID }) => id === programID)
						?.deletedAt,
			),
		[allPrograms, merchantProgramParticipations],
	);

	// update user selection to default (ie all programs)
	useEffect(() => {
		// only update after merchant programs have been fetched
		if (!isFetchingMerchantPrograms) {
			// only update if user selection has not been previously updated (don't want to overwrite a real user's selection)
			if (userSelection.areAllProgramsSelected && userSelection.programs.length === 0) {
				setUserSelection({
					programs: allPrograms,
					areAllProgramsSelected: true,
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allPrograms, isFetchingMerchantPrograms]);

	const programEnrollmentMap = useMemo(() => {
		const hasEcommerceProgram = activePrograms.some(isEcommerceProgram);
		const hasEcommerceProgramWithCheckoutSDKAccess = activePrograms.some(
			(program) => isEcommerceProgram(program) && hasCheckoutSDKAccess(program),
		);
		const hasInstoreProgram = activePrograms.some(isInstoreProgram);
		const hasInstoreVirtualCardProgram = activePrograms.some(
			(program) => isInstoreProgram(program) && isVirtualCardProgram(program),
		);

		return new Map<MerchantProgramEnrollment, boolean>([
			["ecommerce", hasEcommerceProgram],
			["ecommerce-checkout-sdk-access", hasEcommerceProgramWithCheckoutSDKAccess],
			["instore", hasInstoreProgram],
			["instore-virtual-card", hasInstoreVirtualCardProgram],
		]);
	}, [activePrograms]);

	const hasProgramEnrollment = useCallback(
		(enrollment: MerchantProgramEnrollment) => programEnrollmentMap.get(enrollment),
		[programEnrollmentMap],
	);

	return (
		<MerchantProgramsContext.Provider
			value={{
				activePrograms,
				allPrograms,
				merchantProgramParticipations,
				userSelection,
				setUserSelection,
				isFetchingMerchantPrograms,
				hasProgramEnrollment,
			}}
		>
			{children}
		</MerchantProgramsContext.Provider>
	);
};

export const useMerchantPrograms = () => {
	const context = useContext(MerchantProgramsContext);
	if (context === undefined) {
		throw new Error("useMerchantPrograms must be used within a ProgramsProvider");
	}
	return context;
};
